import { getData, getSheet } from '../base/index';
import { DataManager, Query, Deferred, Predicate } from '@syncfusion/ej2-data';
import { getIndexesFromAddress, getSwapRange, getRangeAddress, getRangeIndexes, skipHiddenIdx, getSheetIndexFromAddress } from '../common/index';
import { setRow, getSheetIndex } from '../index';
import { initiateFilter, hideShow } from '../common/event';
/**
 * The `WorkbookFilter` module is used to handle filter action in Spreadsheet.
 */
export class WorkbookFilter {
    /**
     * Constructor for WorkbookFilter module.
     *
     * @param {Workbook} parent - Constructor for WorkbookFilter module.
     */
    constructor(parent) {
        this.parent = parent;
        this.addEventListener();
    }
    /**
     * To destroy the filter module.
     *
     * @returns {void} - To destroy the filter module.
     */
    destroy() {
        this.removeEventListener();
        this.parent = null;
    }
    addEventListener() {
        this.parent.on(initiateFilter, this.initiateFilterHandler, this);
    }
    removeEventListener() {
        if (!this.parent.isDestroyed) {
            this.parent.off(initiateFilter, this.initiateFilterHandler);
        }
    }
    /**
     * Filters a range of cells in the sheet.
     *
     * @param { {args: BeforeFilterEventArgs, promise: Promise<FilterEventArgs>}} eventArgs - Specify the event args.
     * @param {BeforeFilterEventArgs} eventArgs.args - arguments for filtering..
     * @param {Promise<FilterEventArgs>} eventArgs.promise - Specify the promise.
     * @param {boolean} eventArgs.refresh - Specify the refresh.
     * @returns {void} - Filters a range of cells in the sheet.
     */
    initiateFilterHandler(eventArgs) {
        const args = eventArgs.args;
        const deferred = new Deferred();
        const sheet = getSheet(this.parent, getSheetIndexFromAddress(this.parent, args.range));
        const filterOptions = args.filterOptions || {};
        eventArgs.promise = deferred.promise;
        if (filterOptions.datasource) {
            this.setFilter(filterOptions.datasource, filterOptions.predicates, args.range, eventArgs.refresh);
            const filterEventArgs = { range: args.range, filterOptions: filterOptions };
            deferred.resolve(filterEventArgs);
        }
        else {
            const range = getSwapRange(getIndexesFromAddress(args.range));
            if (range[0] > sheet.usedRange.rowIndex || range[1] > sheet.usedRange.colIndex) {
                deferred.reject('Select a cell or range inside the used range and try again.');
                return;
            }
            if (range[0] === range[2] && (range[2] - range[0]) === 0) { //if selected range is a single cell
                range[0] = 0;
                range[1] = 0;
                range[3] = sheet.usedRange.colIndex;
            }
            range[2] = sheet.usedRange.rowIndex; //filter range should be till used range.
            range[0] = range[0] + 1; //ignore first row
            const address = getRangeAddress(range);
            getData(this.parent, `${sheet.name}!${address}`, true, true, null, null, null, null, false).then((jsonData) => {
                const dataManager = new DataManager(jsonData);
                this.setFilter(dataManager, filterOptions.predicates, args.range, eventArgs.refresh);
                const filterEventArgs = { range: address, filterOptions: filterOptions };
                deferred.resolve(filterEventArgs);
            });
        }
    }
    /**
     * Hides or unhides the rows based on the filter predicates.
     *
     * @param {DataManager} dataManager - Specify the dataManager.
     * @param {Predicate[]} predicates - Specify the predicates.
     * @param {string} range - Specify the range.
     * @param {boolean} refresh - Specify the refresh.
     * @returns {void} - Hides or unhides the rows based on the filter predicates.
     */
    setFilter(dataManager, predicates, range, refresh) {
        if (dataManager && predicates) {
            const jsonData = dataManager.dataSource.json;
            const query = new Query();
            if (predicates.length) {
                query.where(Predicate.and(predicates));
            }
            const result = dataManager.executeLocal(query);
            const rowKey = '__rowIndex';
            let sheet;
            let sheetIdx;
            if (range.indexOf('!') > -1) {
                sheetIdx = getSheetIndex(this.parent, range.split('!')[0]);
                sheet = getSheet(this.parent, sheetIdx);
            }
            else {
                sheet = this.parent.getActiveSheet();
                sheetIdx = getSheetIndex(this.parent, sheet.name);
            }
            if (this.parent.getModuleName() === 'spreadsheet') {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                const parent = this.parent;
                let hide;
                let refreshUI;
                if ((parent.scrollSettings.enableVirtualization && (jsonData.length > (parent.viewport.rowCount +
                    (parent.getThreshold('row') * 2))) || sheet.frozenRows || sheet.frozenColumns) || refresh) {
                    jsonData.forEach((data) => {
                        hide = result.indexOf(data) < 0;
                        setRow(sheet, Number(data[`${rowKey}`]) - 1, { hidden: hide, isFiltered: hide });
                    });
                    refreshUI = sheetIdx === parent.activeSheetIndex;
                    const paneIndexes = getRangeIndexes(sheet.paneTopLeftCell);
                    this.parent.updateTopLeftCell(skipHiddenIdx(sheet, paneIndexes[0], true) - this.parent.frozenRowCount(sheet), null, 'col');
                }
                else {
                    jsonData.forEach((data) => {
                        hide = result.indexOf(data) < 0;
                        if (refreshUI) {
                            setRow(sheet, Number(data[`${rowKey}`]) - 1, { hidden: hide, isFiltered: hide });
                        }
                        else {
                            const eventArgs = { startIndex: Number(data[`${rowKey}`]) - 1, hide: hide,
                                isFiltering: true, sheetIndex: sheetIdx };
                            eventArgs.endIndex = eventArgs.startIndex;
                            this.parent.notify(hideShow, eventArgs);
                            refreshUI = eventArgs.refreshUI;
                        }
                    });
                }
                if (refreshUI) {
                    parent.renderModule.refreshSheet(false, false, true);
                }
            }
            else {
                let hide;
                jsonData.forEach((data) => {
                    hide = result.indexOf(data) < 0;
                    setRow(sheet, Number(data[`${rowKey}`]) - 1, { hidden: hide, isFiltered: hide });
                });
            }
        }
    }
    /**
     * Gets the module name.
     *
     * @returns {string} - Get the module name.
     */
    getModuleName() {
        return 'workbookFilter';
    }
}
